import { type ComponentProps } from 'react';

import {
	DatePicker as ArkDatePicker,
	type DatePickerRootProps as ArkDatePickerRootProps
} from '@ark-ui/react';

import type { Radius, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type DatePickerRootProps = ArkDatePickerRootProps & {
	size?: Scale3;
};

function DatePickerRoot({ className, size = '2', ...props }: DatePickerRootProps) {
	return (
		<ArkDatePicker.Root className={cn('pt-date-picker', className)} data-size={size} {...props} />
	);
}

function DatePickerView({ className, ...props }: ComponentProps<typeof ArkDatePicker.View>) {
	return <ArkDatePicker.View className={cn('pt-date-picker-view', className)} {...props} />;
}

function DatePickerViewControl({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.ViewControl>) {
	return (
		<ArkDatePicker.ViewControl
			className={cn('pt-date-picker-view-control', className)}
			{...props}
		/>
	);
}

function DatePickerViewTrigger({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.ViewTrigger>) {
	return (
		<ArkDatePicker.ViewTrigger
			className={cn('pt-date-picker-view-trigger', className)}
			{...props}
		/>
	);
}

function DatePickerPrevTrigger({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.PrevTrigger>) {
	return (
		<ArkDatePicker.PrevTrigger
			className={cn('pt-date-picker-prev-trigger', className)}
			{...props}
		/>
	);
}

function DatePickerNextTrigger({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.NextTrigger>) {
	return (
		<ArkDatePicker.NextTrigger
			className={cn('pt-date-picker-next-trigger', className)}
			{...props}
		/>
	);
}

function DatePickerRangeText({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.RangeText>) {
	return (
		<ArkDatePicker.RangeText className={cn('pt-date-picker-range-text', className)} {...props} />
	);
}

function DatePickerTable({ className, ...props }: ComponentProps<typeof ArkDatePicker.Table>) {
	return <ArkDatePicker.Table className={cn('pt-date-picker-table', className)} {...props} />;
}

function DatePickerTableHead({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.TableHead>) {
	return (
		<ArkDatePicker.TableHead className={cn('pt-date-picker-table-head', className)} {...props} />
	);
}

function DatePickerTableHeader({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.TableHeader>) {
	return (
		<ArkDatePicker.TableHeader
			className={cn('pt-date-picker-table-header', className)}
			{...props}
		/>
	);
}

function DatePickerTableBody({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.TableBody>) {
	return (
		<ArkDatePicker.TableBody className={cn('pt-date-picker-table-body', className)} {...props} />
	);
}

function DatePickerTableRow({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.TableRow>) {
	return (
		<ArkDatePicker.TableRow className={cn('pt-date-picker-table-row', className)} {...props} />
	);
}

function DatePickerTableCell({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.TableCell>) {
	return (
		<ArkDatePicker.TableCell className={cn('pt-date-picker-table-cell', className)} {...props} />
	);
}

type DatePickerTableCellTriggerProps = ComponentProps<typeof ArkDatePicker.TableCellTrigger> & {
	variant?: 'solid' | 'soft';
	radius?: Radius;
};

function DatePickerTableCellTrigger({
	className,
	variant = 'solid',
	radius,
	...props
}: DatePickerTableCellTriggerProps) {
	return (
		<ArkDatePicker.TableCellTrigger
			className={cn('pt-date-picker-cell-trigger', className)}
			data-variant={variant}
			data-radius={radius}
			{...props}
		/>
	);
}

function DatePickerPresetTrigger({
	className,
	...props
}: ComponentProps<typeof ArkDatePicker.PresetTrigger>) {
	return (
		<ArkDatePicker.PresetTrigger
			className={cn('pt-date-picker-preset-trigger', className)}
			{...props}
		/>
	);
}

const DatePicker = Object.assign(DatePickerRoot, {
	Root: DatePickerRoot,
	View: DatePickerView,
	ViewControl: DatePickerViewControl,
	ViewTrigger: DatePickerViewTrigger,
	PrevTrigger: DatePickerPrevTrigger,
	NextTrigger: DatePickerNextTrigger,
	RangeText: DatePickerRangeText,
	Table: DatePickerTable,
	TableHead: DatePickerTableHead,
	TableHeader: DatePickerTableHeader,
	TableBody: DatePickerTableBody,
	TableRow: DatePickerTableRow,
	TableCell: DatePickerTableCell,
	TableCellTrigger: DatePickerTableCellTrigger,
	PresetTrigger: DatePickerPresetTrigger,
	Context: ArkDatePicker.Context
});

export { DatePicker, type DatePickerRootProps, type DatePickerTableCellTriggerProps };
